import { Component, EventEmitter, Input, Output } from '@angular/core';
import { getStoredUser } from '../helpers/user-storage.util';
import { CommonModule } from '@angular/common';
import { ModalService } from '../services/modal.service';
import { ProposalService } from '../services/proposal.service';
import { ToastrService } from 'ngx-toastr';
import { ProjectService } from '../services/project.service';
import { RequirementsAcknowledgementComponent } from '../requirements-acknowledgement/requirements-acknowledgement.component';
import { projectHasSiteRequirements } from '../utils/site-requirements';
import { ModalComponent } from '../shared/modal/modal.component';
import { buildBulkProjectDetail } from '../list/bulk-eligibility.util';
import { ProjectActionLockService, lockIds } from '../services/project-action-lock.service';
import { BulkItemListComponent } from '../bulk-item-list/bulk-item-list.component';

@Component({
  selector: 'accept-offered-proposal-dialog',
  standalone: true,
  imports: [
    CommonModule,
    RequirementsAcknowledgementComponent,
    ModalComponent,
    BulkItemListComponent,
  ],
  templateUrl: './accept-offered-proposal-dialog.component.html',
  styleUrl: './accept-offered-proposal-dialog.component.scss',
})
export class AcceptOfferedProposalDialogComponent {
  @Input() active: boolean = false;
  @Input() multiple: boolean = false;
  /**
   * The UNFILTERED project. Feeds `specialQuoteRequirements`,
   * `hasAnySiteRequirements` and the Project ID summary, so the
   * requirements-acknowledgement gate keeps its whole-project scope — a
   * special site condition on a site that is NOT part of this bulk accept
   * still has to be acknowledged. Narrowing this to the eligible set would
   * silently narrow that gate; the filtered set goes in `siteDetails` below.
   */
  @Input() data: any = '';
  /**
   * The products this accept actually writes, when the host is sweeping a
   * subset of the project (the bulk Accept All Offers path). Left null by the
   * single-accept hosts (`product-line`, `proposal-details`), which already
   * pass a one-site `data` and must keep posting it verbatim — hence the
   * guard in `acceptOffer()` rather than an unconditional rebuild.
   */
  @Input() siteDetails: any[] | null = null;
  /**
   * How many products the bulk confirmation sentence names. Supplied by the
   * host — NOT derived here at init: on Project Overview `data` arrives
   * asynchronously after the dialog is constructed and the dialog is not
   * wrapped in an `*ngIf`, so an init-time derivation read a null `data` once
   * and left the body reading "accepting undefined work assignment(s)"
   * forever. `allbids-dialog`'s `numberOfBids` takes the same shape.
   *
   * The host's count deliberately excludes RE_OFFERED — a revised fee is a
   * negotiated figure the PA should accept on the single-line Accept path,
   * where both figures ("Revised offer" / "You asked") are visible, not swept
   * into a bulk accept-all. See `acceptAllOffers` in
   * `list/bulk-eligibility.util.ts`, which encodes the same exclusion.
   */
  @Input() numberOfAcceptedProposals: number = 0;
  /**
   * Names where a bulk accept applies, appended to the confirmation sentence
   * ("at Site 2"). Set by a site card's Accept All Offers; the banner leaves
   * it empty, so its sentence reads as before.
   */
  @Input() scopeLabel = '';
  @Input() siteType: string = '';
  @Input() siteSubType: string = '';
  @Input() address: any = {
    streetAddress: '',
    cityStateZip: '',
  };
  @Output() modalClosed = new EventEmitter<boolean>();
  @Output() acceptSingleOffer = new EventEmitter<any>();

  isSubmitting = false;
  user = getStoredUser();

  get hasAnySiteRequirements(): boolean {
    return projectHasSiteRequirements(this.data);
  }

  constructor(
    private modalService: ModalService,
    private proposalService: ProposalService,
    private projectService: ProjectService,
    private toastr: ToastrService,
    private lock: ProjectActionLockService
  ) {}

  /**
   * The ProjectDetail this accept posts: the full project with only the
   * products being accepted, when the host narrowed the set; otherwise `data`
   * verbatim, which is what the single-accept hosts pass and must keep
   * posting.
   */
  private get writePayload(): any {
    return this.siteDetails
      ? buildBulkProjectDetail(this.data, this.siteDetails)
      : this.data;
  }

  toggleOpenAcceptedOfferedProposalDialog() {
    // Only reached from the bulk branch below, but the plurality is passed
    // explicitly rather than hardcoded: the confirmation is hosted by the
    // page, not by whatever triggered the accept, so it cannot tell on its
    // own whether one offer or all of them were accepted.
    this.modalService.toggleOpenOfferAcceptedModal(this.multiple);
  }

  closeDialog() {
    this.active = false;
    this.modalClosed.emit(this.active);
  }

  acceptOffer() {
    this.isSubmitting = true;
    const payload = this.writePayload;
    this.lock
      .hold(lockIds(payload), this.proposalService.acceptOffer(payload, this.user.id))
      .subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          let apiResponse: any = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const data = apiResponse.data;
            if (data) {
              if (this.multiple) {
                this.projectService.updateProjectDataSubjectWithProjectDetail(
                  data
                );
                this.closeDialog();
                this.toggleOpenAcceptedOfferedProposalDialog();
              } else {
                // emit to proposal detail page to open the confirmation dialog, this
                // prevents the same dialog from being open on the main dashboard
                this.projectService.updateProjectDataSubjectWithSingleSite(data);
                this.acceptSingleOffer.emit();
              }
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        }
      });
  }
}
